import { useEffect, useState } from 'react';
import { HexColorInput } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { Input, MultiInput, MultiSelect, SingleSelect, SpinnerInput } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import {
  Breadcrumb,
  Button,
  ColorPicker,
  Label,
  LABEL_TYPE,
  Spinner,
  Textarea
} from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { VideoThemeType } from '@/enums';
import { PRIVATE_PAGE_ROUTES } from '@/enums/RouterEnums';
import { generateOrderOptions, showToast, ToastType } from '@/helpers';
import { createUpdateThemeValidation } from '@/pages/Themes/ThemeValidation';
import { ArrowDownHeavyIcon, ArrowUpHeavyIcon, VideoThemeIcon } from '@/resources';
import { BuildingsIcon } from '@/resources/icons/sidebar';
import {
  useCreateVideoThemeMutation,
  useGetAllOrganizations,
  useGetAllVideoTheme,
  useGetVideoThemeById,
  useUpdateVideoThemeMutation
} from '@/services';
import {
  CreateEditOrganization,
  CreateEditThemePayload,
  HexColor,
  OrganizationBasic,
  SelectOptionProps
} from '@/types';

export const CreateEditTheme = () => {
  const { themeId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation('common');

  const [error, setError] = useState<number>();
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const [color, setColor] = useState('#ffffff');
  const [selectedOrganizations, setSelectedOrganizations] = useState<SelectOptionProps[]>([]);
  const [searchOrganization, setSearchOrganization] = useState<string>('');
  const isEditPage = !!themeId;

  const { data: themeData, isLoading: isThemeLoading } = useGetVideoThemeById(Number(themeId), {
    onSuccess: () => {
      undefined;
    },
    onError: (error) => {
      setError(error.status);
    }
  });

  const { mutate: createTheme, isLoading: isCreating } = useCreateVideoThemeMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.THEMES);
      showToast(ToastType.SUCCESS, t('themeCreated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: updateTheme, isLoading: isUpdating } = useUpdateVideoThemeMutation(queryClient, {
    onSuccess: () => {
      navigate(`${PRIVATE_PAGE_ROUTES.THEMES}/${themeId}`);
      showToast(ToastType.SUCCESS, t('themeUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { data: allThemes } = useGetAllVideoTheme(PAGE_SIZE);

  const {
    data: allOrganizations,
    isLoading: isLoadingOrganizations,
    fetchNextPage: fetchNextOrganizationsPage,
    hasNextPage: hasNextOrganizationsPage
  } = useGetAllOrganizations(PAGE_SIZE, searchOrganization);

  const isSubmitting = isCreating || isUpdating;
  const pageTitle = isEditPage ? t('editThemeDetails') : t('addTheme');

  const count = allThemes?.pages[0].data?.items.length || 0;
  const orderOptions = generateOrderOptions(count, isEditPage);

  const breadcrumbPath = isEditPage
    ? [
        {
          label: themeData?.data.name,
          url: PRIVATE_PAGE_ROUTES.SINGLE_VIDEO_THEME.replace(':themeId', themeData?.data?.id)
        },
        { label: t('editThemeDetails') }
      ]
    : [{ label: t('addTheme') }];

  const themeTypeOptions = [
    { value: VideoThemeType.Public, label: t('public') },
    { value: VideoThemeType.Premium, label: t('premium') }
  ];

  const organizationTypeOption = { value: VideoThemeType.Organization, label: t('organization') };

  // Type to fall back to once the last organization is removed: the theme's own type before it
  // became org-scoped, or Public for a new theme / one that was already Organization-typed.
  const originalType =
    themeData?.data.type !== undefined && themeData.data.type !== VideoThemeType.Organization
      ? themeData.data.type
      : VideoThemeType.Public;

  const getOrganizationOptions = () => {
    return allOrganizations?.pages.flatMap((page) =>
      page.data?.items.map((org: CreateEditOrganization) => ({
        value: org.id,
        label: org.name
      }))
    ) as SelectOptionProps[];
  };

  const handleOrganizationChange = (
    value: SelectOptionProps[],
    setFieldValue: (field: string, value: SelectOptionProps[] | number[] | number) => void
  ) => {
    setSelectedOrganizations(value);
    const organizationIds = Array.isArray(value)
      ? value.map((m) => Number(m?.value)).filter((id) => !isNaN(id))
      : [];
    setFieldValue('organizationIds', organizationIds);
    if (organizationIds.length === 0) {
      setFieldValue('type', originalType);
    }
  };

  const handleOrganizationRemove = (
    org: SelectOptionProps,
    setFieldValue: (field: string, value: SelectOptionProps[] | number[] | number) => void
  ) => {
    if ((selectedOrganizations as SelectOptionProps[])?.includes(org)) {
      const updated = selectedOrganizations.filter((o) => o?.value !== org?.value);
      setSelectedOrganizations(updated);
      const organizationIds = updated.map((m) => Number(m?.value)).filter((id) => !isNaN(id));
      setFieldValue('organizationIds', organizationIds);
      if (organizationIds.length === 0) {
        setFieldValue('type', originalType);
      }
    }
  };

  const initialValues: CreateEditThemePayload = {
    id: themeData?.data.id,
    name: themeData?.data.name || '',
    description: themeData?.data.description || '',
    tags: themeData?.data.tags || [],
    musicStartDelay: themeData?.data.musicStartDelay || 0,
    musicEndDelay: themeData?.data.musicEndDelay || 0,
    barsColor: themeData?.data.barsColor,
    order: themeData?.data.order || 0,
    type: themeData?.data.type ?? VideoThemeType.Public,
    organizationIds: themeData?.data.organizations?.map((o: OrganizationBasic) => o.id) || []
  };

  const handleSubmit = (values: CreateEditThemePayload) => {
    const organizationIds = values.organizationIds ?? [];
    const payload = {
      id: values.id,
      name: values.name,
      description: values.description,
      tags: values.tags.map((tag) => ({ name: tag })),
      thumbnailUrl: themeData?.data.thumbnailUrl || '',
      musicStartDelay: values.musicStartDelay || 0,
      musicEndDelay: values.musicEndDelay || 0,
      order: values.order || 0,
      videoRenderingProjectId: themeData?.data.videoRenderingProjectId || null,
      barsColor: color.replace(/^#/, '').slice(0, 6),
      hasShadow: values.hasShadow ?? true,
      isPublish: false,
      type:
        organizationIds.length > 0
          ? VideoThemeType.Organization
          : (values.type ?? VideoThemeType.Public),
      organizationIds
    };

    isEditPage ? updateTheme(payload) : createTheme(payload);
  };

  const handleCancel = () => {
    navigate(isEditPage ? `${PRIVATE_PAGE_ROUTES.THEMES}/${themeId}` : PRIVATE_PAGE_ROUTES.THEMES);
  };

  useEffect(() => {
    if (themeData?.data.barsColor) {
      setColor(`#${themeData?.data.barsColor.replace(/^#/, '')}`);
    }
  }, [themeData]);

  useEffect(() => {
    if (isEditPage && themeData?.data.organizations?.length > 0) {
      setSelectedOrganizations(
        themeData?.data.organizations.map((o: OrganizationBasic) => ({
          value: o.id,
          label: o.name
        }))
      );
    } else {
      setSelectedOrganizations([]);
    }
  }, [themeData]);

  return (
    <div className='flex h-full justify-center'>
      {isEditPage && isThemeLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        <div className='mx-3 mt-7 flex w-full flex-col md:mx-20'>
          <div className='w-full'>
            <Breadcrumb
              icon={<VideoThemeIcon className='stroke-[0.5] text-lg text-primary-blue' />}
              iconPath={PRIVATE_PAGE_ROUTES.THEMES}
              path={breadcrumbPath}
            />
          </div>
          <div className='mx-3 flex flex-col md:mt-9'>
            <Label text={pageTitle} type={LABEL_TYPE.H1} />
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={createUpdateThemeValidation}>
              {({ isValid, dirty, setFieldValue, values, errors, touched }) => (
                <Form id='createEditTheme'>
                  <div className='mt-2 flex flex-col md:mt-5'>
                    <Input
                      id='name'
                      label={t('themeName')}
                      type='text'
                      isRequired
                      width={'md:w-1/2 w-full'}
                      className='rounded-lg'
                      errorMessage={touched.name ? errors.name?.toString() : undefined}
                      isInvalid={!!(errors.name && touched.name)}
                    />
                  </div>
                  <div className='w-full md:flex md:w-1/2 md:space-x-4'>
                    <div className='mt-3 w-full md:mt-5 md:w-1/3'>
                      <SpinnerInput
                        label={t('musicStartDelay')}
                        labelClasses='block w-full truncate'
                        value={values.musicStartDelay || 0}
                        onChange={(value) => setFieldValue('musicStartDelay', value)}
                      />
                    </div>
                    <div className='mt-3 w-full md:mt-5 md:w-1/3'>
                      <SpinnerInput
                        label={t('musicEndDelay')}
                        labelClasses='block w-full truncate'
                        value={values.musicEndDelay || 0}
                        onChange={(value) => setFieldValue('musicEndDelay', value)}
                      />
                    </div>
                    <div className='mt-3 w-full md:mt-5 md:w-1/4'>
                      <Label
                        text={t('barsColor')}
                        className='mb-2.5 !text-black'
                        type={LABEL_TYPE.PARAGRAPH2}
                      />
                      <div
                        className='relative flex h-10 cursor-pointer items-center justify-between rounded-xl border border-secondary-blue-gray p-2'
                        onClick={() => setIsPickerOpen(!isPickerOpen)}>
                        <div
                          style={{ backgroundColor: color }}
                          className={'size-5 rounded-full border-2'}
                        />
                        {isPickerOpen ? (
                          <ArrowUpHeavyIcon className='size-3 stroke-[0.5] text-black' />
                        ) : (
                          <ArrowDownHeavyIcon className='size-3 stroke-[0.5] text-black' />
                        )}
                      </div>
                      <div className='relative'>
                        {isPickerOpen && (
                          <div className='absolute left-0 z-10 mt-2 rounded-md p-2'>
                            <ColorPicker
                              isOpen={isPickerOpen}
                              setIsOpen={setIsPickerOpen}
                              color={color as HexColor}
                              onChange={(value) => {
                                setFieldValue('barsColor', value);
                                setColor(value);
                              }}
                              showColorPreview={false}
                            />
                            <HexColorInput
                              color={color}
                              onChange={(value) => {
                                setFieldValue('barsColor', value);
                                setColor(value);
                              }}
                              className='absolute -bottom-4 left-0 w-[150px] rounded-md border-2 py-1 text-center outline-none'
                              data-prevent-click-outside={true}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='mt-3 w-full md:mt-5 md:w-1/4'>
                      <SingleSelect
                        id='order'
                        label={t('order')}
                        options={orderOptions}
                        value={{
                          value: values.order ?? 0,
                          label: (values.order ?? 0).toString()
                        }}
                        handleChange={(newValue) => setFieldValue('order', newValue?.value)}
                      />
                    </div>
                  </div>
                  <div className='mt-2 w-full md:w-1/2'>
                    <MultiSelect
                      placeholder={t('selectOrganizations')}
                      id='organizationIds'
                      label={t('organizations')}
                      options={isLoadingOrganizations ? [] : getOrganizationOptions()}
                      handleChange={(value) => handleOrganizationChange(value, setFieldValue)}
                      fetchNextPage={fetchNextOrganizationsPage}
                      hasNextPage={hasNextOrganizationsPage}
                      selectedValues={selectedOrganizations ?? []}
                      handleInputChange={(inputValue: string) => setSearchOrganization(inputValue)}
                      showTags
                      controlShouldRenderValue={false}
                      handleTagRemove={(org) => handleOrganizationRemove(org, setFieldValue)}
                      tagsHeight='max-h-7'
                      tagIcon={BuildingsIcon}
                    />
                  </div>
                  <div className='mt-2 flex w-full md:w-1/2'>
                    <SingleSelect
                      containerClassName='w-full'
                      id='type'
                      label={t('type')}
                      disabled={selectedOrganizations.length > 0}
                      options={
                        selectedOrganizations.length > 0
                          ? [organizationTypeOption]
                          : themeTypeOptions
                      }
                      value={
                        selectedOrganizations.length > 0
                          ? organizationTypeOption
                          : themeTypeOptions.find(
                              (option) => option.value === (values.type ?? VideoThemeType.Public)
                            ) || null
                      }
                      handleChange={(newValue) =>
                        setFieldValue('type', Number(newValue?.value ?? VideoThemeType.Public))
                      }
                    />
                  </div>
                  <div className='mt-5 flex flex-col md:w-1/2'>
                    <MultiInput
                      id='tags'
                      label='Tags'
                      value={values.tags}
                      handleChange={(tags) => setFieldValue('tags', tags)}
                      addButtonLabel={t('add')}
                      placeholder='Tags'
                    />
                  </div>
                  <div className='mt-5 flex flex-col'>
                    <Textarea
                      id='description'
                      label={t('description')}
                      className='h-24 w-full md:w-1/2'
                    />
                  </div>
                  <div className='mt-5 flex w-full justify-end gap-4 md:mt-10 md:w-1/2'>
                    <Button
                      variant={ButtonVariant.QUATERNARY}
                      className='w-full md:w-auto'
                      handleOnClick={handleCancel}>
                      {t('cancel')}
                    </Button>
                    <Button
                      type='submit'
                      className='w-full md:w-auto'
                      variant={ButtonVariant.PRIMARY}
                      disabled={isSubmitting || !(isValid && dirty)}>
                      {isEditPage ? t('saveChanges') : t('addTheme')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};
